/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * The record that binds a local message number to a global one and lays out its
 * fields.
 *
 * FIT data records identify themselves only by a 4-bit local number; the
 * definition that most recently claimed that number says what the bytes mean.
 * An encoder therefore emits a definition whenever the active layout changes.
 *
 * [endianness] is the architecture the definition announces, and it governs every
 * multi-byte value that follows it: the global message number in the definition
 * record itself, and every field of every data record written under it. It is
 * carried through [write] and [Mesg.write], so a BIG definition produces a
 * big-endian record that reads back identically.
 */
public class MesgDefinition(
    public val localMesgNum: UByte,
    public val globalMesgNum: UShort,
    public val endianness: Endianness = Endianness.LITTLE,
    public val fieldDefinitions: List<FieldDefinition> = emptyList(),
    public val developerFieldDefinitions: List<DeveloperFieldDefinition> = emptyList(),
) {
    public val numFields: Int get() = fieldDefinitions.size

    public val numDeveloperFields: Int get() = developerFieldDefinitions.size

    /** Total byte length of one data record using this definition. */
    public val messageSize: Int
        get() = fieldDefinitions.sumOf { it.size } + developerFieldDefinitions.sumOf { it.size }

    public fun write(writer: ByteWriter) {
        var header = Fit.HDR_TYPE_DEF_BIT or (localMesgNum.toInt() and Fit.HDR_TYPE_MASK)
        if (developerFieldDefinitions.isNotEmpty()) header = header or Fit.DEV_DATA_MASK

        writer.writeByte(header.toUByte())
        writer.writeByte(0u) // reserved
        writer.writeByte(endianness.value)
        writer.writeUShort(globalMesgNum, endianness)
        writer.writeByte(numFields.toUByte())
        fieldDefinitions.forEach { it.write(writer) }

        if (developerFieldDefinitions.isNotEmpty()) {
            writer.writeByte(numDeveloperFields.toUByte())
            developerFieldDefinitions.forEach { it.write(writer) }
        }
    }

    /**
     * Definition-level equality, ignoring the local number the layout is bound to.
     *
     * [endianness] counts as part of the layout: two definitions listing the same
     * fields under different architectures decode differently, so an encoder must
     * emit a fresh definition record rather than reuse the bound one.
     */
    public fun hasSameLayout(other: MesgDefinition): Boolean =
        globalMesgNum == other.globalMesgNum &&
            endianness == other.endianness &&
            fieldDefinitions == other.fieldDefinitions &&
            developerFieldDefinitions == other.developerFieldDefinitions

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is MesgDefinition) return false
        return localMesgNum == other.localMesgNum && hasSameLayout(other)
    }

    override fun hashCode(): Int {
        var result = localMesgNum.toInt()
        result = 31 * result + globalMesgNum.toInt()
        result = 31 * result + endianness.hashCode()
        result = 31 * result + fieldDefinitions.hashCode()
        result = 31 * result + developerFieldDefinitions.hashCode()
        return result
    }

    override fun toString(): String =
        "MesgDefinition(local=$localMesgNum, global=$globalMesgNum, fields=$numFields)"

    public companion object {
        /** The layout needed to write [mesg], bound to [localMesgNum] and [endianness]. */
        public fun of(
            mesg: Mesg,
            localMesgNum: UByte,
            endianness: Endianness = Endianness.LITTLE,
        ): MesgDefinition =
            MesgDefinition(
                localMesgNum = localMesgNum,
                globalMesgNum = mesg.globalMesgNum,
                endianness = endianness,
                fieldDefinitions = mesg.fieldList.filter { it.hasValues }.map { FieldDefinition(it) },
                developerFieldDefinitions = mesg.developerFieldList
                    .filter { it.hasValues }
                    .map { DeveloperFieldDefinition(it.fieldNum, it.size, it.developerDataIndex) },
            )

        /**
         * Reads a definition record whose header byte has already been consumed
         * and is passed as [recordHeader].
         */
        public fun read(reader: ByteReader, recordHeader: UByte): MesgDefinition {
            val local = (recordHeader.toInt() and Fit.LOCAL_MESG_NUM_MASK).toUByte()
            reader.readByte() // reserved
            val architecture = reader.readByte()
            val endianness = Endianness.fromValue(architecture)
                ?: throw FitFormatException("unknown architecture byte $architecture", reader.position - 1)
            val globalMesgNum = reader.readUShort(endianness)

            val numFields = reader.readByte().toInt()
            val fieldDefinitions = ArrayList<FieldDefinition>(numFields)
            repeat(numFields) { fieldDefinitions.add(FieldDefinition.read(reader)) }

            val developerFieldDefinitions = mutableListOf<DeveloperFieldDefinition>()
            if ((recordHeader.toInt() and Fit.DEV_DATA_MASK) == Fit.DEV_DATA_MASK) {
                val numDevFields = reader.readByte().toInt()
                repeat(numDevFields) { developerFieldDefinitions.add(DeveloperFieldDefinition.read(reader)) }
            }

            return MesgDefinition(local, globalMesgNum, endianness, fieldDefinitions, developerFieldDefinitions)
        }
    }
}
